import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { toast } from "react-toastify";
import { DollarSign, Save, Search } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;
const ITEMS_PER_PAGE = 25;

const TarifasExamenesPage = () => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [savingCode, setSavingCode] = useState(null);
  const token = localStorage.getItem("token");

  const fetchTarifas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/util/tarifas-examenes-mt01`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(response.data.map((item) => ({ ...item, precioEdit: item.precio ?? 0 })));
    } catch (error) {
      console.error(error);
      toast.error("No fue posible cargar las tarifas de exámenes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTarifas(); }, []); // eslint-disable-line

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (item) => item.examen_cod?.toLowerCase().includes(term) || item.nombre?.toLowerCase().includes(term)
    );
  }, [rows, search]);

  const totalPages = Math.ceil(filteredRows.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentRows = filteredRows.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => { setCurrentPage(1); }, [search]);

  const updatePriceInput = (examen_cod, newValue) => {
    setRows((prev) => prev.map((item) => item.examen_cod === examen_cod ? { ...item, precioEdit: newValue } : item));
  };

  const handleSave = async (item) => {
    const parsedPrice = Number(item.precioEdit);
    if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
      toast.error("Ingrese un precio válido mayor o igual a 0");
      return;
    }
    setSavingCode(item.examen_cod);
    try {
      await axios.put(`${API_URL}/util/tarifas-examenes-mt01/${item.examen_cod}`,
        { precio: parsedPrice },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows((prev) => prev.map((row) =>
        row.examen_cod === item.examen_cod ? { ...row, precio: parsedPrice, precioEdit: parsedPrice } : row
      ));
      toast.success(`Precio actualizado para ${item.examen_cod}`);
    } catch (error) {
      console.error(error);
      toast.error(`No se pudo actualizar el precio de ${item.examen_cod}`);
    } finally {
      setSavingCode(null);
    }
  };

  return (
    <AppLayout>
      <div className="p-6 space-y-5">
        {/* Cabecera */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand-100 rounded-xl">
              <DollarSign className="h-5 w-5 text-brand-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Tarifas de exámenes</h1>
              <p className="text-sm text-gray-500">Gestión de precios de exámenes activos</p>
            </div>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por código o nombre..."
              className="w-full pl-9 pr-4 border border-brand-200 rounded-xl py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-all"
            />
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-2xl border border-brand-100 shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-brand-gradient text-white">
                  {["Código", "Examen", "Precio actual", "Nuevo precio", "Acción"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-50">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center">
                      <div className="flex flex-col items-center gap-3 text-brand-400">
                        <div className="h-7 w-7 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
                        <span className="text-sm">Cargando tarifas...</span>
                      </div>
                    </td>
                  </tr>
                ) : currentRows.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-gray-400 text-sm">
                      No se encontraron exámenes para el filtro aplicado.
                    </td>
                  </tr>
                ) : currentRows.map((item, i) => (
                  <tr key={item.examen_cod} className={`transition-colors ${i % 2 === 0 ? "bg-white" : "bg-brand-50/30"} hover:bg-brand-50`}>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded font-semibold">{item.examen_cod}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-800 font-medium">{item.nombre}</td>
                    <td className="px-4 py-3 text-gray-600">{Number(item.precio || 0).toFixed(2)}</td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.precioEdit}
                        onChange={(e) => updatePriceInput(item.examen_cod, e.target.value)}
                        className="w-36 border border-brand-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-brand-400"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <Button size="sm" onClick={() => handleSave(item)} disabled={savingCode === item.examen_cod} className="gap-1">
                        <Save className="h-3 w-3" />
                        {savingCode === item.examen_cod ? "Guardando..." : "Guardar"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center text-sm text-gray-600 gap-3">
            <span>Mostrando {currentRows.length} de {filteredRows.length} resultados</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}>← Anterior</Button>
              <span className="px-3 py-1.5 bg-brand-50 border border-brand-200 rounded-lg text-brand-700 font-medium">
                {currentPage} / {totalPages || 1}
              </span>
              <Button variant="outline" size="sm" disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((p) => p + 1)}>Siguiente →</Button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default TarifasExamenesPage;
